import type { AspectRatio, VideoResolution } from '../../api/types'
import { nodeSchemas, type RawNodeDataRecord } from './nodeRegistry'

export function supportsWorkspaceStyle(type: string | undefined): boolean {
  if (!type) return false
  return !!nodeSchemas[type]?.inputs.some((input) => input.id === 'in_style')
}

function hasOwnInStyle(data: RawNodeDataRecord | undefined): boolean {
  return !!data && Object.prototype.hasOwnProperty.call(data, 'in_style')
}

/**
 * Stamp the project's configured visual style onto a newly-created style-aware
 * node's `in_style` — the client-side twin of the server stamp in
 * GraphManager._apply_add_node (which covers Director-created nodes). Toolbox
 * adds write straight to YDoc and never hit _apply_add_node, so without this a
 * user-created node would keep the schema default `bold-manhwa` regardless of the
 * project setting.
 *
 * `workspaceStyleId` MUST be the live configured style
 * (`ProjectConfig.style_id` / `WorkflowState.style_id`, the same source the
 * ProjectConfigGear reads/writes) — NOT the stale legacy `Project.style` enum,
 * which is never synced when the user changes style via the gear (that
 * divergence caused Workplace post 1573282384429059). An explicit per-node
 * `in_style` in `explicitData` always wins and is left untouched.
 */
export function applyWorkspaceStyleDefault(
  type: string | undefined,
  data: RawNodeDataRecord | undefined,
  workspaceStyleId: string | undefined,
  explicitData: RawNodeDataRecord | undefined = data,
): RawNodeDataRecord {
  const resolvedData = data ?? {}
  if (!workspaceStyleId || !supportsWorkspaceStyle(type) || hasOwnInStyle(explicitData)) {
    return resolvedData
  }
  return {
    ...resolvedData,
    in_style: workspaceStyleId,
  }
}

export function supportsVideoResolution(type: string | undefined): boolean {
  if (!type) return false
  return !!nodeSchemas[type]?.inputs.some((input) => input.id === 'in_resolution')
}

function hasOwnInResolution(data: RawNodeDataRecord | undefined): boolean {
  return !!data && Object.prototype.hasOwnProperty.call(data, 'in_resolution')
}

/**
 * Stamp the project's persistent "Output Quality" default onto a newly-created
 * video node's `in_resolution` — the client-side twin of the server stamp in
 * GraphManager._apply_add_node (which covers Director-created nodes). Toolbox
 * adds write straight to YDoc and never hit _apply_add_node, so without this a
 * user-created video node would keep the schema default 720p regardless of the
 * project setting.
 *
 * The value is written verbatim: freshly-added toolbox video nodes default to
 * Gemini Omni 1.1 Flash (which supports 720p/1080p/4k), and the
 * VideoGeneratorNode auto-snap effect is the backstop that clamps to a valid
 * resolution for any other model. This used to name veo-3.1, which stopped
 * being true when Omni 1.1 became the default and again when Veo was
 * deprecated.
 * We intentionally add no model->resolution map here — per-model validity is
 * owned by videoConstraints (display) and normalize_resolution (render).
 */
export function applyProjectResolutionDefault(
  type: string | undefined,
  data: RawNodeDataRecord | undefined,
  projectVideoResolution: VideoResolution | undefined,
  explicitData: RawNodeDataRecord | undefined = data,
): RawNodeDataRecord {
  const resolvedData = data ?? {}
  if (
    !projectVideoResolution ||
    !supportsVideoResolution(type) ||
    hasOwnInResolution(explicitData)
  ) {
    return resolvedData
  }
  return {
    ...resolvedData,
    in_resolution: projectVideoResolution,
  }
}


export function supportsAspectRatio(type: string | undefined): boolean {
  if (!type) return false
  return !!nodeSchemas[type]?.inputs.some((input) => input.id === 'in_aspect_ratio')
}

function hasOwnInAspectRatio(data: RawNodeDataRecord | undefined): boolean {
  return !!data && Object.prototype.hasOwnProperty.call(data, 'in_aspect_ratio')
}

/**
 * Stamp the project's aspect ratio onto a newly-created node's
 * `in_aspect_ratio` — the third client-side twin of the server stamp in
 * GraphManager._apply_add_node, alongside style and resolution.
 *
 * This one was missing for a long time, and the omission was invisible because
 * the other two were present: `in_aspect_ratio` carries a schema default of
 * "16:9", so a toolbox-added node on a 9:16 project silently rendered
 * landscape while its style and resolution were correct. Confirmed on dev-meta
 * before the fix — project 9:16, toolbox Image Generator reported
 * `in_aspect_ratio=16:9`, `in_style=cinematic-real-person`.
 *
 * Director-created nodes are NOT affected and must stay that way: RFC-0580
 * requires them to OMIT the field so the server fills it from WorkflowState at
 * creation. This helper exists only because toolbox adds write straight to
 * YDoc and never reach that fill.
 */
export function applyProjectAspectRatioDefault(
  type: string | undefined,
  data: RawNodeDataRecord | undefined,
  projectAspectRatio: AspectRatio | undefined,
  explicitData: RawNodeDataRecord | undefined = data,
): RawNodeDataRecord {
  const resolvedData = data ?? {}
  if (
    !projectAspectRatio ||
    !supportsAspectRatio(type) ||
    hasOwnInAspectRatio(explicitData)
  ) {
    return resolvedData
  }
  return {
    ...resolvedData,
    in_aspect_ratio: projectAspectRatio,
  }
}
